import { CommentPanelStatus } from "components/common/comments/interface";
import { cloneDeep } from "lodash";
import { eHierarchyLevel } from "../../functions/api/enums";
import { getThisAndParentHierarchies } from "../../functions/getParentHierarchies";
import { getHierarchyPrefix } from "./getHierarchyPrefix";
import { HierarchyContextProps, IHierarchyActionLoadHierarchy } from "./interface";
import { mergeClinicalLibraryData } from "./mergeClinicalLibraryData";

const templateLevels: (eHierarchyLevel | undefined)[] = [
  eHierarchyLevel.TopLevel,
  eHierarchyLevel.Corporate,
  eHierarchyLevel.TA,
  eHierarchyLevel.Project,
];
// const studyLevels: (eHierarchyLevel | undefined)[] = [
//   eHierarchyLevel.Study,
//   eHierarchyLevel.Report,
// ];
/**
 * Determines whether a hierarchy level is a template or shell.
 *
 * @param level - The hierarchy level to check
 * @returns "Template" if the level is a template level, otherwise "Shell"
 */
const levelType = (level: eHierarchyLevel) => {
  return templateLevels.includes(level) ? "Template" : "Shell";
};

/**
 * Loads hierarchy data into the context state and builds parent templates.
 *
 * @param action - The action containing the hierarchy and parent nodes to load
 * @param state - The current hierarchy context state
 * @returns Updated hierarchy context state with loaded hierarchy and templates
 */
export const hcr_loadHierarchy = (
  action: IHierarchyActionLoadHierarchy,
  state: HierarchyContextProps,
): HierarchyContextProps => {
  // Update parent hierarchy information
  const hierarchyNodes = [
    ...state.hierarchyNodes.filter(
      (h) => ![...action.hierarchyNodes!.map((h) => h.id), action.hierarchy!.id].includes(h.id),
    ),
    action.hierarchy,
    ...action.hierarchyNodes.filter((h) => h.level !== eHierarchyLevel.TopLevel),
  ];

  // Build parent templates immutably
  const parents = getThisAndParentHierarchies(action.hierarchy.parentId, hierarchyNodes);
  const parentsWithLevelType = parents.map((ph) => ({
    ...ph,
    levelType: templateLevels.includes(ph.level) ? "Template" : "Shell",
  }));

  // Build parent templates arrays
  const parentTemplates = parentsWithLevelType.reduce(
    (acc, ph) => {
      // Add in new domain replacements from this hierarchy
      if (levelType(action.hierarchy!.level) === ph.levelType) {
        acc.domainReplacements = [
          ...acc.domainReplacements,
          ...ph.domainReplacements.filter(
            (dr) =>
              !acc.domainReplacements.some(
                (r) => r.domain === dr.domain && r.oldText === dr.oldText,
              ),
          ),
        ];
      }

      // Add in new external list replacements from this hierarchy
      if (levelType(action.hierarchy!.level) === ph.levelType) {
        acc.lists = [
          ...acc.lists,
          ...ph.lists
            .filter(
              (list) =>
                !acc.lists.some(
                  (l) =>
                    l.givenName ===
                    (list.givenName ??
                      // istanbul ignore next
                      "blank"),
                ),
            )
            .map((list) => ({ ...list, fromHierarchyId: ph.id })),
        ];
      }

      // Add in new row groups from this hierarchy - no level type filter
      acc.rowGroups = [
        ...acc.rowGroups,
        ...ph.rowGroups
          .filter(
            (rowGroup) =>
              !acc.rowGroups.some(
                (rg) =>
                  rg.name ===
                  (rowGroup.name ??
                    // istanbul ignore next
                    "blank"),
              ),
          )
          .map((rg) => ({ ...rg, fromHierarchyId: ph.id })),
      ];

      // Get current single names
      if (levelType(action.hierarchy!.level) === ph.levelType) {
        acc.singles = [
          ...acc.singles,
          ...ph.singles
            .filter((single) => !acc.singles.some((s) => s.oldText === single.oldText))
            .map((single) => ({ ...single, fromHierarchyId: ph.id })),
        ];
      }

      // Add clinical library data from parent hierarchies with matching level type
      if (levelType(action.hierarchy!.level) === ph.levelType && ph.clinicalLibrary) {
        const mergedClinicalData = mergeClinicalLibraryData(
          {
            clinicalDatasets: acc.clinicalDatasets,
            clinicalCodeLists: acc.clinicalCodeLists,
            clinicalValueLists: acc.clinicalValueLists,
            clinicalComments: acc.clinicalComments,
            clinicalMethods: acc.clinicalMethods,
            clinicalWhereClauses: acc.clinicalWhereClauses,
          },
          ph.clinicalLibrary,
        );
        return {
          ...acc,
          ...mergedClinicalData,
        };
      }

      return acc;
    },
    {
      clinicalDatasets: [] as typeof state.parentTemplates.clinicalDatasets,
      clinicalCodeLists: [] as typeof state.parentTemplates.clinicalCodeLists,
      clinicalValueLists: [] as typeof state.parentTemplates.clinicalValueLists,
      clinicalComments: [] as typeof state.parentTemplates.clinicalComments,
      clinicalMethods: [] as typeof state.parentTemplates.clinicalMethods,
      clinicalWhereClauses: [] as typeof state.parentTemplates.clinicalWhereClauses,
      domainReplacements: [] as typeof state.parentTemplates.domainReplacements,
      lists: [] as typeof state.parentTemplates.lists,
      rowGroups: [] as typeof state.parentTemplates.rowGroups,
      singles: [] as typeof state.parentTemplates.singles,
    },
  );

  // Reset Comment Panel Status for initial load if needed
  const commentPanelStatus: CommentPanelStatus = state.commentPanelStatus.showComments
    ? {
        showComments: false,
        highlightCommentId: "",
        activeRelatedTo: "",
        commentFilter: "all",
      }
    : state.commentPanelStatus;

  return {
    ...state,
    changed: false,
    id: action.hierarchy.id,
    hierarchy: action.hierarchy,
    savedHierarchy: cloneDeep(action.hierarchy),
    prefix: getHierarchyPrefix(action.hierarchy.level),
    hierarchyNodes,
    parentTemplates,
    accessRights: action.accessRights,
    tlfItemIndex: null,
    isSubscribed: action.isSubscribed,
    commentPanelStatus,
  };
};
